<script setup lang="ts">
import { computed } from "vue";

interface Source {
    domain: string;
    source: string;
}

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
});

defineEmits(["close"]);

const location = computed(() => {
    return [
        props.item.geo?.city,
        props.item.geo?.region,
        props.item.geo?.country,
    ]
        .filter(Boolean)
        .join(", ");
});

const ipinfoUrl = computed(() => {
    return `https://ipinfo.io/${props.item.ip}`;
});

const sources = computed((): Source[] => props.item.sources ?? []);

function ipDetailUrl(domain: string, source: string): string {
    return `/reports/${encodeURIComponent(domain)}/ip/${encodeURIComponent(source)}/${encodeURIComponent(props.item.ip)}`;
}
</script>

<template>
    <article class="ip-card">
        <button type="button" class="ip-card__close" @click="$emit('close')">
            ×
        </button>

        <h3 class="ip-card__title">{{ item.label }} — {{ item.ip }}</h3>

        <p class="ip-card__org">
            {{ item.geo?.org || "—" }}
        </p>

        <p v-if="location" class="ip-card__location">
            {{ location }}
        </p>

        <p v-if="item.geo?.timezone" class="ip-card__timezone">
            {{ item.geo.timezone }}
        </p>

        <a
            class="ip-card__link"
            :href="ipinfoUrl"
            target="_blank"
            rel="noopener noreferrer"
        >
            ipinfo.io/{{ item.ip }}
        </a>

        <div v-if="sources.length" class="ip-card__sources">
            <p class="ip-card__sources-label">Senders</p>
            <ul class="ip-card__sources-list">
                <li
                    v-for="src in sources"
                    :key="src.domain + '|' + src.source"
                    class="ip-card__source-item"
                >
                    <a
                        :href="ipDetailUrl(src.domain, src.source)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ip-card__source-link"
                    >
                        <span class="ip-card__source-domain">{{
                            src.domain
                        }}</span>
                        <span class="ip-card__source-sep"> / </span>
                        <span class="ip-card__source-name">{{
                            src.source
                        }}</span>
                    </a>
                </li>
            </ul>
        </div>
    </article>
</template>

<style scoped>
.ip-card {
    position: relative;
    padding: 14px 16px;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 12px 36px rgb(0 0 0 / 18%);
    font-size: 14px;
}

.ip-card__close {
    position: absolute;
    top: 8px;
    right: 10px;
    border: 0;
    background: transparent;
    font-size: 20px;
    cursor: pointer;
}

.ip-card__title {
    margin: 0 24px 8px 0;
    font-size: 15px;
    font-weight: 700;
}

.ip-card__org,
.ip-card__location,
.ip-card__timezone {
    margin: 4px 0;
}

.ip-card__sources {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e5e7eb;
}

.ip-card__sources-label {
    margin: 0 0 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
}

.ip-card__sources-list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.ip-card__source-item {
    margin: 3px 0;
}

.ip-card__source-link {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    max-width: 260px;
    color: #1565c0;
    text-decoration: none;
    font-size: 13px;
    line-height: 1.4;
    word-break: break-all;
}

.ip-card__source-link:hover {
    text-decoration: underline;
}

.ip-card__source-domain {
    font-weight: 600;
}

.ip-card__source-sep {
    color: #9ca3af;
}

.ip-card__source-name {
    color: #374151;
}

.ip-card__link {
    display: inline-block;
    margin-top: 10px;
    color: #1565c0;
}
</style>
